import React, { useState } from "react";
import { Box, Typography } from "@mui/material";
import { LuChevronDown, LuChevronUp, LuCalendarDays } from "react-icons/lu";
import { formattedAmountWithoutAed } from "../../../utils/CommonFunction";
import {
    renderCurrencyIcon,
    getCurrentCurrencyLocale,
} from "../../../utils/CurrencyUtils";
import Moodboards from "./Moodboards";
import { phaseImage } from "../constants";

const SectionTitle = ({ children }) => (
    <Typography
        sx={{
            fontSize: "15px",
            fontWeight: 600,
            color: "#0d0d0e",
            fontFamily: "Inter Tight",
            marginBottom: "10px",
        }}
    >
        {children}
    </Typography>
);

const TIMELINE_COLORS = [
    "#4D6BF6",
    "#FA832E",
    "#22C55E",
    "#A855F7",
    "#EC4899",
];

const deliverableWeeks = (d) =>
    d.weeks !== "" && d.weeks != null && Number(d.weeks) > 0
        ? Number(d.weeks)
        : null;

// Week ranges for the timeline track — sequential, laid out back-to-back
// using each deliverable's entered week count.
const calculateWeekRanges = (deliverables) => {
    const withWeeks = deliverables.filter((d) => deliverableWeeks(d) != null);
    if (withWeeks.length === 0) return [];
    let cursor = 1;
    return withWeeks.map((d, i) => {
        const weeks = deliverableWeeks(d);
        const startWeek = cursor;
        const endWeek = startWeek + weeks - 1;
        cursor = endWeek + 1;
        return {
            phase: d.name,
            startWeek,
            endWeek,
            weeks,
            color: TIMELINE_COLORS[i % TIMELINE_COLORS.length],
        };
    });
};

// Read-only deliverable accordion row — mirrors the admin portal's design
// bid review Deliverables tab.
function ReviewDeliverableRow({ deliverable }) {
    const [expanded, setExpanded] = useState(false);
    const subs = deliverable.sub_deliverables || [];
    const hasBody = subs.length > 0 || !!deliverable.description;
    const weeks = deliverableWeeks(deliverable);
    const revisions =
        deliverable.revisions !== "" && deliverable.revisions != null
            ? Number(deliverable.revisions)
            : deliverable.metadata?.revisions ?? null;

    return (
        <Box
            sx={{
                border: "1px solid #ececee",
                borderRadius: "12px",
                backgroundColor: "#fff",
                marginBottom: "8px",
                overflow: "hidden",
            }}
        >
            <Box
                onClick={hasBody ? () => setExpanded((v) => !v) : undefined}
                sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: "12px",
                    padding: "12px 14px",
                    cursor: hasBody ? "pointer" : "default",
                    "&:hover": hasBody
                        ? { backgroundColor: "#fafafb" }
                        : undefined,
                }}
            >
                <img
                    src={phaseImage(deliverable.name)}
                    alt={deliverable.name}
                    style={{
                        width: "36px",
                        height: "36px",
                        borderRadius: "8px",
                        objectFit: "cover",
                        flexShrink: 0,
                    }}
                />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                        sx={{
                            fontSize: "14px",
                            fontWeight: 500,
                            color: "#0d0d0e",
                            fontFamily: "Inter Tight",
                        }}
                    >
                        {deliverable.name}
                    </Typography>
                    {revisions != null && (
                        <Typography
                            sx={{
                                fontSize: "12.5px",
                                color: "#949daa",
                                fontFamily: "Inter Tight",
                            }}
                        >
                            Revisions - {revisions}
                        </Typography>
                    )}
                </Box>
                {weeks != null && (
                    <Box
                        sx={{
                            display: "inline-flex",
                            alignItems: "center",
                            padding: "4px 12px",
                            borderRadius: "999px",
                            backgroundColor: "#EFF2FE",
                            color: "#4D6BF6",
                            fontSize: "12.5px",
                            fontWeight: 500,
                            whiteSpace: "nowrap",
                            fontFamily: "Inter Tight",
                        }}
                    >
                        {weeks} week{weeks === 1 ? "" : "s"}
                    </Box>
                )}
                {subs.length > 0 && (
                    <Box
                        sx={{
                            display: "inline-flex",
                            alignItems: "center",
                            padding: "4px 12px",
                            borderRadius: "999px",
                            backgroundColor: "#F7F7F7",
                            color: "#949daa",
                            fontSize: "12.5px",
                            fontWeight: 500,
                            whiteSpace: "nowrap",
                            fontFamily: "Inter Tight",
                        }}
                    >
                        {subs.length}{" "}
                        {subs.length === 1 ? "deliverable" : "deliverables"}
                    </Box>
                )}
                {hasBody && (
                    <Box sx={{ color: "#949daa", display: "flex" }}>
                        {expanded ? (
                            <LuChevronUp size={18} />
                        ) : (
                            <LuChevronDown size={18} />
                        )}
                    </Box>
                )}
            </Box>
            {expanded && hasBody && (
                <Box
                    sx={{
                        padding: "10px 16px 14px 62px",
                        borderTop: "1px solid #F3F4F6",
                        backgroundColor: "#fafafb",
                    }}
                >
                    {deliverable.description && (
                        <Typography
                            sx={{
                                fontSize: "13px",
                                color: "#949daa",
                                whiteSpace: "pre-wrap",
                                fontFamily: "Inter Tight",
                                marginBottom: "4px",
                            }}
                        >
                            {deliverable.description}
                        </Typography>
                    )}
                    {subs.length > 0 && (
                        <>
                            <Typography
                                sx={{
                                    fontSize: "14px",
                                    fontWeight: 500,
                                    color: "#0d0d0e",
                                    fontFamily: "Inter Tight",
                                    marginTop: "10px",
                                    marginBottom: "8px",
                                }}
                            >
                                Deliverables
                            </Typography>
                            <Box component="ul" sx={{ margin: 0, paddingLeft: "20px" }}>
                                {subs.map((s, idx) => (
                                    <Box
                                        component="li"
                                        // eslint-disable-next-line react/no-array-index-key
                                        key={idx}
                                        sx={{
                                            fontSize: "14px",
                                            color: "#949daa",
                                            fontWeight: 500,
                                            marginBottom: "6px",
                                            fontFamily: "Inter Tight",
                                        }}
                                    >
                                        {s}
                                    </Box>
                                ))}
                            </Box>
                        </>
                    )}
                </Box>
            )}
        </Box>
    );
}

// Tab 04 — read-only review of everything before submission.
export default function DesignReview({
    project,
    proposalAmount,
    proposalDescription,
    deliverables = [],
    paymentRows = [],
    moodboards = [],
    projectImageUrl,
}) {
    const totalAmount = Number(proposalAmount) || 0;
    const weekRanges = calculateWeekRanges(deliverables);

    return (
        <Box
            sx={{
                display: "flex",
                flexDirection: "column",
                gap: "24px",
                margin: "1vh 0",
            }}
        >
            {/* Project header */}
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: "16px",
                    border: "1px solid #E5E7EB",
                    borderRadius: "12px",
                    padding: "16px",
                }}
            >
                {projectImageUrl && (
                    <img
                        src={projectImageUrl}
                        alt={project?.name}
                        style={{
                            width: "130px",
                            height: "130px",
                            borderRadius: "12px",
                            objectFit: "cover",
                            flexShrink: 0,
                        }}
                    />
                )}
                <Box
                    sx={{
                        flex: 1,
                        minWidth: 0,
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "center",
                    }}
                >
                    <Typography
                        sx={{
                            fontSize: "18px",
                            fontWeight: 600,
                            color: "#0d0d0e",
                            fontFamily: "Inter Tight",
                        }}
                    >
                        {project?.name || "-"}
                    </Typography>
                    {(project?.start_date || project?.end_date) && (
                        <Typography
                            sx={{
                                fontSize: "13px",
                                color: "#949daa",
                                fontFamily: "Inter Tight",
                                marginBottom: "6px",
                            }}
                        >
                            {project?.start_date || "—"} –{" "}
                            {project?.end_date || "—"}
                        </Typography>
                    )}
                    {proposalDescription && (
                        <Typography
                            sx={{
                                fontSize: "13.5px",
                                color: "#374151",
                                fontFamily: "Inter Tight",
                                whiteSpace: "pre-wrap",
                                display: "-webkit-box",
                                WebkitBoxOrient: "vertical",
                                WebkitLineClamp: 4,
                                overflow: "hidden",
                            }}
                        >
                            {proposalDescription}
                        </Typography>
                    )}
                </Box>
                <Box sx={{ textAlign: "right" }}>
                    <Typography
                        sx={{
                            fontSize: "12px",
                            color: "#949daa",
                            fontFamily: "Inter Tight",
                        }}
                    >
                        Proposal Amount
                    </Typography>
                    <Typography
                        component="span"
                        sx={{
                            fontSize: "18px",
                            fontWeight: 600,
                            color: "#0d0d0e",
                            display: "inline-flex",
                            alignItems: "center",
                            gap: "4px",
                            fontFamily: "Inter Tight",
                        }}
                    >
                        {totalAmount ? (
                            <>
                                {renderCurrencyIcon("black-small")}
                                {formattedAmountWithoutAed(totalAmount)}
                            </>
                        ) : (
                            "-"
                        )}
                    </Typography>
                </Box>
            </Box>

            {/* Moodboards */}
            {moodboards.length > 0 && (
                <Moodboards
                    projectId={project?.id}
                    initialMoodboards={moodboards}
                    disabled
                />
            )}

            {/* Deliverables timeline — same track visual as the execution
                review tab, driven by deliverable start/end dates */}
            {weekRanges.length > 0 && (
                <Box>
                    <SectionTitle>
                        <Box
                            component="span"
                            sx={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: "8px",
                            }}
                        >
                            <LuCalendarDays size={18} color="#4D6BF6" />
                            Deliverables Timeline
                        </Box>
                    </SectionTitle>
                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: { xs: "column", md: "row" },
                            backgroundColor: "#fff",
                            border: "1px solid #e5e7eb",
                            borderRadius: "12px",
                            overflow: "hidden",
                        }}
                    >
                        {weekRanges.map((range, i) => (
                            <Box
                                // eslint-disable-next-line react/no-array-index-key
                                key={i}
                                sx={{
                                    flex: { md: range.weeks },
                                    padding: "14px",
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: "6px",
                                    position: "relative",
                                    borderRight: {
                                        md:
                                            i === weekRanges.length - 1
                                                ? "none"
                                                : "1px solid #f3f4f6",
                                    },
                                    borderBottom: {
                                        xs:
                                            i === weekRanges.length - 1
                                                ? "none"
                                                : "1px solid #f3f4f6",
                                        md: "none",
                                    },
                                    "&::before": {
                                        content: '""',
                                        position: "absolute",
                                        top: 0,
                                        left: 0,
                                        width: "100%",
                                        height: "3px",
                                        background: range.color,
                                    },
                                }}
                            >
                                <Typography
                                    sx={{
                                        fontSize: "13px",
                                        fontWeight: 600,
                                        color: range.color,
                                        fontFamily: "Inter Tight",
                                    }}
                                >
                                    Week{" "}
                                    {range.startWeek === range.endWeek
                                        ? range.startWeek
                                        : `${range.startWeek}-${range.endWeek}`}
                                </Typography>
                                <Typography
                                    sx={{
                                        fontSize: "13px",
                                        fontWeight: 500,
                                        color: "#0d0d0e",
                                        fontFamily: "Inter Tight",
                                    }}
                                >
                                    {range.phase}
                                </Typography>
                                <Typography
                                    sx={{
                                        fontSize: "11px",
                                        color: "#6b7280",
                                        fontFamily: "Inter Tight",
                                    }}
                                >
                                    {range.weeks}{" "}
                                    {range.weeks === 1 ? "week" : "weeks"}
                                </Typography>
                            </Box>
                        ))}
                    </Box>
                </Box>
            )}

            {/* Deliverables */}
            <Box>
                <SectionTitle>Deliverables</SectionTitle>
                {deliverables.length === 0 ? (
                    <Typography
                        sx={{
                            fontSize: "13px",
                            color: "#949daa",
                            fontFamily: "Inter Tight",
                        }}
                    >
                        No deliverables added.
                    </Typography>
                ) : (
                    deliverables.map((d, i) => (
                        <ReviewDeliverableRow
                            key={d.id || `new-${i}`}
                            deliverable={d}
                        />
                    ))
                )}
            </Box>

            {/* Payments */}
            <Box>
                <SectionTitle>Payments</SectionTitle>
                {paymentRows.length === 0 ? (
                    <Typography
                        sx={{
                            fontSize: "13px",
                            color: "#949daa",
                            fontFamily: "Inter Tight",
                        }}
                    >
                        No payment structure selected.
                    </Typography>
                ) : (
                    // Same table as reno-portal's view-bid-details Payments
                    // tab: Payment Type | Percentage (%) | Amount (AED),
                    // with a right-aligned total below.
                    <Box sx={{ overflowX: "auto", maxWidth: "768px" }}>
                        <Box
                            component="table"
                            sx={{
                                width: "100%",
                                borderCollapse: "collapse",
                                fontFamily: "Inter Tight",
                                "& th": {
                                    fontSize: "12px",
                                    fontWeight: 400,
                                    color: "#949daa",
                                    padding: "10px 14px",
                                    textAlign: "left",
                                },
                                "& td": {
                                    fontSize: "14px",
                                    color: "#0d0d0e",
                                    padding: "12px 14px",
                                },
                            }}
                        >
                            <Box component="thead">
                                <Box component="tr">
                                    <Box component="th">Payment Type</Box>
                                    <Box component="th">Percentage (%)</Box>
                                    <Box
                                        component="th"
                                        sx={{ textAlign: "right !important" }}
                                    >
                                        Amount ({getCurrentCurrencyLocale()})
                                    </Box>
                                </Box>
                            </Box>
                            <Box component="tbody">
                                {paymentRows.map((p, idx) => {
                                    const amount = totalAmount
                                        ? (totalAmount *
                                              (Number(p.percentage) || 0)) /
                                          100
                                        : null;
                                    return (
                                        <Box
                                            component="tr"
                                            key={p.group_id || idx}
                                        >
                                            <Box component="td">
                                                {p.name || "-"}
                                            </Box>
                                            <Box component="td">
                                                {p.percentage != null
                                                    ? `${p.percentage}`
                                                    : "-"}
                                            </Box>
                                            <Box
                                                component="td"
                                                sx={{ textAlign: "right" }}
                                            >
                                                {amount != null
                                                    ? formattedAmountWithoutAed(
                                                          amount,
                                                      )
                                                    : "-"}
                                            </Box>
                                        </Box>
                                    );
                                })}
                            </Box>
                        </Box>
                        <Box
                            sx={{
                                display: "flex",
                                justifyContent: "flex-end",
                                marginTop: "16px",
                                paddingRight: "16px",
                            }}
                        >
                            <Typography
                                sx={{
                                    fontSize: "15px",
                                    fontWeight: 500,
                                    color: "#0d0d0e",
                                    fontFamily: "Inter Tight",
                                }}
                            >
                                Total Amount ({getCurrentCurrencyLocale()}):{" "}
                                {totalAmount
                                    ? formattedAmountWithoutAed(totalAmount)
                                    : "-"}
                            </Typography>
                        </Box>
                    </Box>
                )}
            </Box>
        </Box>
    );
}
